/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	TablePagination,
} from "@material-ui/core";
import { ExternalContactsTableRow } from "./";
import { useExternalContacts } from "../../context/externalContacts";
import usePagination from "../../../../hooks/usePagination";

/**
 * Editable table of external contacts
 */
export const ExternalContactsTable = ({ setIsLoading }) => {
	const {
		contactsCount,
		filteredContacts,
		deleteContacts,
		showDeleteConfirmation,
		cancelDeleteConfirmation,
		toggleSelectAllContacts,
		toggleSelectContact,
		areAllContactsSelected,
		selectedContactIds,
		selectedContactsCount,
		startEditContact,
	} = useExternalContacts();

	const { setPageNo, setRowsPerPage, pageNo, dataSlice, rowsPerPage } = usePagination(filteredContacts);

	const handleDeleteContact = React.useCallback(
		async (contactId) => {
			setIsLoading(true);
			await deleteContacts([contactId]);
			setIsLoading(false);
		},
		[deleteContacts, setIsLoading],
	);

	const renderRow = React.useCallback(
		(contact) => {
			return (
				<ExternalContactsTableRow
					key={contact.contactId}
					contact={contact}
					toggleSelectContactId={toggleSelectContact}
					editContact={startEditContact}
					isSelected={selectedContactIds.includes(contact.contactId)}
					deleteContact={() =>
						showDeleteConfirmation(
							1,
							() => handleDeleteContact(contact.contactId),
							() => cancelDeleteConfirmation(),
						)
					}
				/>
			);
		},
		[
			cancelDeleteConfirmation,
			showDeleteConfirmation,
			toggleSelectContact,
			handleDeleteContact,
			selectedContactIds,
			startEditContact,
		],
	);

	const rows = React.useMemo(() => {
		if (!dataSlice.length) {
			return null;
		}
		return dataSlice.map((contact) => renderRow(contact));
	}, [dataSlice, renderRow]);

	return (
		<div>
			<TableContainer component={Paper} elevation={0}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell style={{ width: 72 }}>
								<Checkbox
									className="px-0"
									indeterminate={selectedContactsCount > 0 && selectedContactsCount < contactsCount}
									checked={areAllContactsSelected}
									onChange={toggleSelectAllContacts}
								/>
							</TableCell>
							<TableCell>Name</TableCell>
							<TableCell>Phone</TableCell>
							<TableCell>Tags</TableCell>
							<TableCell>Azure AD User ID</TableCell>
							<TableCell>Available for Teams conversation</TableCell>
							<TableCell style={{ width: 100 }}></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!filteredContacts.length ? (
							<TableRow>
								<TableCell colSpan={7} style={{ height: 60 }} align="center">
									No contacts found.
								</TableCell>
							</TableRow>
						) : (
							rows
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				count={filteredContacts.length}
				rowsPerPage={rowsPerPage}
				page={pageNo}
				onPageChange={(e, pageNo) => setPageNo(pageNo)}
				onRowsPerPageChange={(rowsPerPageCnt) => {
					setRowsPerPage(parseInt(rowsPerPageCnt.currentTarget.innerText) || 10);
					setPageNo(0);
				}}
			/>
		</div>
	);
};
